import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading } from "../../components/VB";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Settings as SettingsIcon, DollarSign, Clock } from "lucide-react";

// Halaman "Listing" — mirror bentuk PaymentDeveloper.jsx/PaymentDesigner.jsx (Rekap + Pengaturan),
// TAPI tidak ada payroll/pembayaran IDR sama sekali (beda dari Developer/Designer) — tab
// "Rekap Status" murni mengelompokkan status "Sudah/Belum Listing" PER PETUGAS yang bertugas,
// supaya penandaan status tidak lagi lepas tanpa pemilik. Kontribusi harga listing ke project
// (Rate Harga Listing) sudah ditambahkan langsung ke total_payment lewat ProjectPayment.jsx.
const usd = (n) => "$ " + (parseFloat(n || 0)).toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: 2 });

// Sentinel Type Web untuk order Material Only (order tanpa pembuatan website sama sekali) —
// HARUS sama persis dengan MATERIAL_ONLY_TYPE_WEB_LABEL di backend (server.py) dan
// MATERIAL_ONLY_TYPE_WEB di ProjectPayment.jsx — dipakai sebagai salah satu opsi Type Web yang
// bisa dipilih saat mengatur Rate Harga Listing di sini.
const MATERIAL_ONLY_TYPE_WEB = "Material Only";

const getCurrentPeriode = () => {
  const today = new Date().getDate();
  return today <= 15 ? "15" : "30";
};

export default function PaymentListing() {
  const [tab, setTab] = useState("rekap"); // rekap, carry, settings
  const [recap, setRecap] = useState([]);
  const [listings, setListings] = useState([]);
  const [types, setTypes] = useState([]);
  const [listingRates, setListingRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [carryList, setCarryList] = useState([]);
  const [carryLoading, setCarryLoading] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [filterBulan, setFilterBulan] = useState(today.slice(0, 7));
  const [filterPeriode, setFilterPeriode] = useState(getCurrentPeriode);

  const load = async () => {
    setLoading(true);
    try {
      const [r, l, t, lr] = await Promise.all([
        api.get("/listing-recap", { params: { bulan: filterBulan, periode: filterPeriode } }),
        api.get("/listings"), api.get("/web-types"),
        api.get("/listing-rates"),
      ]);
      setRecap(r.data); setListings(l.data); setTypes(t.data); setListingRates(lr.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterBulan, filterPeriode]);

  const loadCarry = async () => {
    setCarryLoading(true);
    try {
      const r = await api.get("/projects", { params: { listing_carry_status: "Menunggu", all_periode: true } });
      setCarryList(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setCarryLoading(false);
  };
  useEffect(() => { if (tab === "carry") loadCarry(); /* eslint-disable-next-line */ }, [tab]);

  const carryAction = async (projectId, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await api.patch(`/projects/${projectId}/listing-carry`, { action });
      toast.success(action === "carry_again" ? "Dimajukan ke periode berikutnya" : "Ditandai Tanpa Listing");
      loadCarry();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markSudahListing = async (projectId) => {
    try {
      await api.patch(`/projects/${projectId}/listing-status`, { listing_status: "Sudah Listing" });
      toast.success("Status listing diubah: Sudah Listing");
      loadCarry();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleStatus = async (projectId, currentStatus) => {
    const next = currentStatus === "Sudah Listing" ? "Belum Listing" : "Sudah Listing";
    try {
      await api.patch(`/projects/${projectId}/listing-status`, { listing_status: next });
      toast.success(`Status listing diubah: ${next}`);
      load();
      if (detail) setDetail({ ...detail, projects: detail.projects.map((p) => p.id === projectId ? { ...p, listing_status: next } : p) });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const totalProjects = recap.reduce((s, r) => s + (r.count || 0), 0);
  const totalSudah = recap.reduce((s, r) => s + (r.count_sudah || 0), 0);
  const totalBelum = recap.reduce((s, r) => s + (r.count_belum || 0), 0);

  return (
    <Window
      title="Listing"
      actions={
        <>
          <button className={`vb-btn ${tab === "rekap" ? "vb-btn-primary" : ""}`} onClick={() => setTab("rekap")} data-testid="tab-listing-rekap">Rekap Status</button>
          <button className={`vb-btn ${tab === "carry" ? "vb-btn-primary" : ""}`} onClick={() => setTab("carry")} data-testid="tab-listing-carry"><Clock className="w-3 h-3 inline mr-1" />Menunggu Next Periode</button>
          <button className={`vb-btn ${tab === "settings" ? "vb-btn-primary" : ""}`} onClick={() => setTab("settings")} data-testid="tab-listing-settings"><SettingsIcon className="w-3 h-3 inline mr-1" />Pengaturan</button>
        </>
      }
    >
      {tab === "rekap" && (
        <>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 8 }}>
            Status "Sudah/Belum Listing" tiap project dikelompokkan per periode &amp; per petugas Listing yang bertugas (field "Petugas Listing" di Payment Website → Tambah Project Baru) — supaya jelas siapa yang bertanggung jawab menyelesaikan listing project mana.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <select
              className="vb-select"
              style={{ width: 140 }}
              value={filterPeriode}
              onChange={(e) => setFilterPeriode(e.target.value)}
              data-testid="listing-filter-periode"
            >
              <option value="15">Periode 1-15</option>
              <option value="30">Periode 16-30/31</option>
              <option value="all">Semua Periode</option>
            </select>
            <input
              type="month"
              className="vb-input"
              style={{ width: 160 }}
              value={filterBulan}
              onChange={(e) => setFilterBulan(e.target.value)}
              data-testid="listing-filter-bulan"
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginBottom: 12 }}>
            <div className="vb-statcard"><div className="vb-statcard-label">Total Project</div><div className="vb-statcard-value" style={{ color: "#003399", fontSize: 16 }}>{totalProjects}</div></div>
            <div className="vb-statcard"><div className="vb-statcard-label">Sudah Listing</div><div className="vb-statcard-value" style={{ color: "#4f8a10", fontSize: 16 }}>{totalSudah}</div></div>
            <div className="vb-statcard"><div className="vb-statcard-label">Belum Listing</div><div className="vb-statcard-value" style={{ color: "#d8000c", fontSize: 16 }}>{totalBelum}</div></div>
          </div>

          {loading ? <Loading /> : recap.length === 0 ? <EmptyState message="Belum ada project dengan Petugas Listing pada periode/bulan ini. Coba ganti filter periode, atau pilih Petugas Listing pada menu Payment Website." /> : (
            <div style={{ overflowX: "auto" }}>
              <table className="vb-table">
                <thead>
                  <tr>
                    <th>Periode (Due 15/30)</th>
                    <th>Petugas Listing</th>
                    <th style={{ textAlign: "center" }}>Jml Project</th>
                    <th style={{ textAlign: "center" }}>Sudah Listing</th>
                    <th style={{ textAlign: "center" }}>Belum Listing</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {recap.map((r) => (
                    <tr key={`${r.listing_id}|${r.periode}`}>
                      <td style={{ fontWeight: 700, color: "#003399" }}>{r.periode}</td>
                      <td><strong>{r.listing_nama}</strong></td>
                      <td style={{ textAlign: "center" }}>
                        <button className="vb-btn" style={{ padding: "1px 6px" }} onClick={() => setDetail(r)} data-testid={`listing-detail-${r.listing_id}-${r.periode}`}>{r.count} ▾</button>
                      </td>
                      <td style={{ textAlign: "center", color: "#4f8a10", fontWeight: 700 }}>{r.count_sudah}</td>
                      <td style={{ textAlign: "center", color: "#d8000c", fontWeight: 700 }}>{r.count_belum}</td>
                      <td>
                        {r.count_belum === 0 ? (
                          <span className="vb-badge vb-badge-approved"><CheckCircle2 className="w-3 h-3 inline mr-1" />Semua Selesai</span>
                        ) : (
                          <span className="vb-badge vb-badge-rejected">{r.count_belum} Belum Selesai</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "carry" && (
        <>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 8, padding: 6, background: "#fffbe6", border: "1px solid #d4a017" }}>
            Project di sini sudah dibayar Full untuk komponen selain Listing (mode pembayaran "Next Periode Listing") dan menunggu listing-nya selesai. Tandai "Sudah Listing" kalau sudah selesai, "Next Listing" untuk memajukan ke periode berikutnya lagi kalau masih belum selesai, atau "Tanpa Listing" untuk melepas kebutuhan listing project ini secara permanen (project akan hilang dari daftar ini dan tidak ditagih listing lagi).
          </div>
          {carryLoading ? <Loading /> : carryList.length === 0 ? <EmptyState message="Tidak ada project yang sedang menunggu Next Periode Listing." /> : (
            <div style={{ overflowX: "auto" }}>
              <table className="vb-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Domain</th>
                    <th>Petugas Listing</th>
                    <th>Next Periode</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {carryList.map((p) => (
                    <tr key={p.id}>
                      <td><strong>{p.nama_project}</strong></td>
                      <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{p.domain}</td>
                      <td>{p.listing_nama || "-"}</td>
                      <td>
                        <span className="vb-badge" style={{ background: "#dbeafe", color: "#1e3a8a", border: "1px solid #93c5fd", fontSize: 10, fontWeight: 700 }}>
                          {p.listing_carry_periode || "-"}
                        </span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 3 }} onClick={() => markSudahListing(p.id)} data-testid={`carry-sudah-${p.id}`}>✓ Sudah Listing</button>
                        <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 3 }} onClick={() => carryAction(p.id, "carry_again")} data-testid={`carry-next-${p.id}`}>⏭ Next Listing</button>
                        <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => carryAction(p.id, "cancel", `Lepas kebutuhan listing project "${p.nama_project}" secara permanen? Biaya listing pada total project ini akan dihapus.`)} data-testid={`carry-cancel-${p.id}`}>Tanpa Listing</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "settings" && (
        <>
          <ListingSettingSimple items={listings} reload={load} />
          <RateListingSettings types={types} listingRates={listingRates} reload={load} />
        </>
      )}

      {/* Detail Dialog — bisa langsung toggle status dari sini */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} title={`Detail Project — ${detail?.listing_nama || ""} (${detail?.periode || ""})`} width={620}
        footer={<button className="vb-btn" onClick={() => setDetail(null)}>Tutup</button>}>
        {detail && (
          <table className="vb-table">
            <thead><tr><th>Project</th><th>Domain</th><th>Type</th><th>Status</th><th>Aksi</th></tr></thead>
            <tbody>
              {detail.projects.map((p) => {
                const sudah = p.listing_status === "Sudah Listing";
                return (
                  <tr key={p.id}>
                    <td><strong>{p.nama_project}</strong></td>
                    <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{p.domain}</td>
                    <td>{p.type_web}</td>
                    <td>
                      {sudah ? (
                        <span className="vb-badge" style={{ background: "#7bc043", color: "white", border: "1px solid #4f8a10", fontSize: 10, fontWeight: 700 }}>Sudah Listing</span>
                      ) : (
                        <span className="vb-badge" style={{ background: "#e8c000", color: "#5f4300", border: "1px solid #b89a00", fontSize: 10, fontWeight: 700 }}>Belum Listing</span>
                      )}
                    </td>
                    <td>
                      <button className="vb-btn" style={{ padding: "2px 6px" }} onClick={() => toggleStatus(p.id, p.listing_status)} data-testid={`listing-toggle-${p.id}`}>
                        {sudah ? "↩ Belum Listing" : "✓ Sudah Listing"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Dialog>
    </Window>
  );
}

// Master data "User Listing" — mirror SimpleSetting Developer/Designer di ProjectPayment.jsx,
// disalin lokal supaya file ini tetap berdiri sendiri (tidak import komponen private file lain).
function ListingSettingSimple({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const empty = { nama: "", kontak: "", email: "" };
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);

  const submit = async () => {
    try {
      if (editId) await api.put(`/listings/${editId}`, form);
      else await api.post("/listings", form);
      toast.success("Tersimpan"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/listings/${del.id}`); toast.success("Dihapus"); setDel(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <h3 className="vb-h1" style={{ margin: 0 }}>User Listing (Petugas Listing)</h3>
        <button className="vb-btn-primary" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-listing">
          <Plus className="w-3 h-3 inline mr-1" />Tambah
        </button>
      </div>
      {items.length === 0 ? <EmptyState message="Belum ada petugas listing. Tambahkan dulu supaya bisa dipilih di Tambah Project Baru." /> : (
        <table className="vb-table">
          <thead><tr><th>Nama</th><th>Kontak</th><th>Email</th><th>Aksi</th></tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td><strong>{it.nama}</strong></td>
                <td>{it.kontak || "-"}</td>
                <td>{it.email || "-"}</td>
                <td>
                  <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => { setForm(it); setEditId(it.id); setOpen(true); }} data-testid={`edit-listing-${it.id}`}>Edit</button>
                  <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title={editId ? "Edit Petugas Listing" : "Tambah Petugas Listing"}
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={submit} data-testid="listing-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Nama*</label>
          <input className="vb-input" value={form.nama || ""} onChange={(e) => setForm({ ...form, nama: e.target.value })} required data-testid="listing-nama" /></div>
        <div className="vb-form-row"><label className="vb-label">Kontak</label>
          <input className="vb-input" value={form.kontak || ""} onChange={(e) => setForm({ ...form, kontak: e.target.value })} /></div>
        <div className="vb-form-row"><label className="vb-label">Email</label>
          <input className="vb-input" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message={`Hapus petugas listing "${del?.nama}"?`} />
    </div>
  );
}

// Rate Harga Listing — per Type Web, USD, upsert-by-type_web (sama pola /listing-rates backend).
function RateListingSettings({ types, listingRates, reload }) {
  const [open, setOpen] = useState(false);
  const empty = { type_web: "", rate_usd: 0 };
  const [form, setForm] = useState(empty);
  const [del, setDel] = useState(null);

  const submit = async () => {
    try {
      await api.post("/listing-rates", { type_web: form.type_web, rate_usd: parseFloat(form.rate_usd || 0) });
      toast.success("Rate tersimpan"); setOpen(false); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/listing-rates/${del.id}`); toast.success("Dihapus"); setDel(null); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <DollarSign className="w-3.5 h-3.5" style={{ color: "#003399" }} />
        <h3 className="vb-h1" style={{ margin: 0 }}>Rate Harga Listing (per Type Web)</h3>
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 8, padding: 6, background: "#fffbe6", border: "1px solid #d4a017" }}>
        💡 Rate ini ditambahkan LANGSUNG ke harga total project (Total Payment yang ditagihkan ke client), persis seperti Price Domain / Price Web — bukan gaji internal petugas listing.
        Pilih opsi "{MATERIAL_ONLY_TYPE_WEB}" untuk mengatur Rate Listing khusus order tipe Material Only.
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="vb-btn-primary" onClick={() => { setForm(empty); setOpen(true); }} data-testid="add-listing-rate"><Plus className="w-3 h-3 inline mr-1" />Tambah / Update Rate</button>
      </div>
      {listingRates.length === 0 ? <EmptyState message="Belum ada rate. Set rate agar harga listing terhitung otomatis di total project." /> : (
        <table className="vb-table">
          <thead><tr><th>Type Web</th><th style={{ textAlign: "right" }}>Rate (USD)</th><th>Aksi</th></tr></thead>
          <tbody>
            {listingRates.map((it) => (
              <tr key={it.id}>
                <td><strong>{it.type_web}</strong></td>
                <td style={{ textAlign: "right", fontWeight: 600, color: "#003399" }}>{usd(it.rate_usd)}</td>
                <td><button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)}><Trash2 className="w-3 h-3" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title="Set Rate Harga Listing"
        footer={<><button className="vb-btn" onClick={() => setOpen(false)}>Batal</button><button className="vb-btn-primary" onClick={submit} disabled={!form.type_web} data-testid="listing-rate-save">Simpan</button></>}>
        <div className="vb-form-row"><label className="vb-label">Type Web*</label>
          <select className="vb-select" value={form.type_web} onChange={(e) => setForm({ ...form, type_web: e.target.value })} data-testid="listing-rate-type">
            <option value="">- pilih -</option>
            {types.map((t) => <option key={t.id} value={t.nama}>{t.nama}</option>)}
            <option value={MATERIAL_ONLY_TYPE_WEB}>{MATERIAL_ONLY_TYPE_WEB} (order tanpa website)</option>
          </select></div>
        <div className="vb-form-row"><label className="vb-label">Rate (USD)*</label>
          <input type="number" step="0.01" className="vb-input" value={form.rate_usd} onChange={(e) => setForm({ ...form, rate_usd: e.target.value })} data-testid="listing-rate-usd" /></div>
      </Dialog>

      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={doDelete} message="Hapus rate ini?" />
    </div>
  );
}
